class Klaviyo {
  constructor() {
    // Parse configuration from the 'klaviyo-config' element
    const configElement = document.getElementById('klaviyo-config');
    
    if (configElement) {
      try {
        this.json = JSON.parse(configElement.textContent);
      } catch (e) {
        console.error('Failed to parse klaviyo-config JSON:', e);
        this.json = null;
      }
    } else {
      console.warn('No klaviyo-config element found.');
      this.json = null;
    }
       
    this.feedbackTemplate = document.getElementById('klaviyo-feedback-template');
    this.messageContainer = document.createElement('div');
    this.messageContainer.attachShadow({ mode: 'open' });

    this.bisForm = null;
    this.newsletterForms = [];
    this.eventListeners = []; // store event listeners for easy removal (reinit)
  }
  
  /**
   * Initializes the Klaviyo forms and event listeners.
   * Should be called on page load and whenever forms are dynamically updated.
   */
  init() {
    this.destroy(); // Clean up previous initialization

    // Ensure that configuration JSON is valid
    if (typeof this.json !== 'object' || this.json === null) {
      return;
    }
    
    // Initialize the back-in-stock (BIS) form and newsletter forms based on selectors from the config
    this.bisForm = document.getElementById('subscribe-klaviyo-bis');
    this.newsletterForms = document.querySelectorAll(this.json.newsletter_form_selector || '');

    // Initialize newsletter forms if list ID is provided and forms are found
    if (this.json.newsletter_klaviyo_list_id && this.newsletterForms.length > 0) {
      this.hijackNewsletterForms();
    }
    
    // Initialize BIS form if required parameters are present
    if (this.bisForm && this.json.bis && this.json.klaviyo_public_api_key) {
      this.initBisForm();
    }
  }
  
  /**
   * Removes all event listeners and resets form references.
   * Should be called before re-initializing forms to prevent duplicate listeners.
   */
  destroy() {
    // Remove all event listeners
    this.eventListeners.forEach(({ element, type, listener }) => {
      element.removeEventListener(type, listener);
    });
    this.eventListeners = [];
    
    // Reset properties
    this.bisForm = null;
    this.newsletterForms = [];
  }
  
  /**
   * Initializes the Back-in-Stock (BIS) form by adding necessary event listeners.
   * Handles both standard HTML forms and custom forms with buttons and inputs.
   */
  initBisForm() {
    if (this.bisForm instanceof HTMLFormElement) {
      const submitListener = this.onBisFormSubmit.bind(this);
      this.bisForm.addEventListener('submit', submitListener);
      this.eventListeners.push({ element: this.bisForm, type: 'submit', listener: submitListener });
    } else {
      // Handle non-form BIS components
      const bisSubmit = this.bisForm.querySelector('button[type="button"]');
      const emailInput = this.bisForm.querySelector('input[type="email"]');
      
      if (bisSubmit) {
        const clickListener = (event) => {
          event.preventDefault();
          this.onBisFormSubmit(event);
        };
        bisSubmit.addEventListener('click', clickListener);
        this.eventListeners.push({ element: bisSubmit, type: 'click', listener: clickListener });
      }
      
      // Allow fake-form element to submit on Enter
      if (emailInput) {
        const keydownListener = (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.onBisFormSubmit(event);
          }
        };
        emailInput.addEventListener('keydown', keydownListener);
        this.eventListeners.push({ element: emailInput, type: 'keydown', listener: keydownListener });
      }
    }
  }
  
  /**
   * Handles the submission of the BIS form.
   * Sends a subscription request to Klaviyo's API.
   * @param {Event} event - The event object from the form submission.
   */
  async onBisFormSubmit(event) {
    this.bisForm.classList.add('klaviyo-loading');
    
    if (event) {
      event.preventDefault();
    }
  
    // Retrieve email and variant ID from the form
    const email = this.bisForm.querySelector(this.bisFields.email).value;
    const variantId = this.bisForm.querySelector('input[name="variant_id"]').value;
  
    // Prepare the payload for the API request
    const payload = {
      'data': {
        'type': 'back-in-stock-subscription',
        'attributes': {
          'profile': {
            'data': {
              'type': 'profile',
              'attributes': {
                'email': email
              }
            }
          },
          'channels': ['EMAIL'],
        },
        'relationships': {
          'variant': {
            'data': {
              'type': 'catalog-variant',
              'id': `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    };
  
    try {
      // Send the subscription request to Klaviyo
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.json.klaviyo_public_api_key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-06-15'
        },
        body: JSON.stringify(payload),
      });
      this.onBisSubscribed({ success: response.ok, email });
    } catch (error) {
      console.error(error);
      this.onBisSubscribed({ success: false, email });
    } finally {
      this.bisForm.classList.remove('klaviyo-loading');
    }
  }
  
  /**
   * Handles the response after attempting to subscribe to BIS.
   * Displays a success or error message and removes the form.
   * @param {Object} param0 - An object containing the success status.
   */
  async onBisSubscribed({ success, email }) {
    const message = success 
      ? "You are now subscribed to this variant!"
      : "An error occurred. Please try again.";
    this.showMessage(success, message, this.bisForm);
    
    if (success) {
      await this.addCustomFieldsToBisProfile({ email });
    }
  }
  
  /**
   * Add custom prop to BIS profiles
   * https://developers.klaviyo.com/en/v1-2/reference/identify-get
   * @param {String} email
   */
  async addCustomFieldsToBisProfile({ email }) {    
    const customFields = {};

    this.bisFields.custom.forEach(field => {
      const fieldElement = this.bisForm.querySelector(field.selector);

      if (fieldElement) {
        customFields[field.name] = fieldElement.value;
      }
    });

    if (!Object.keys(customFields).length) return;

    const updateObj = {
      "token": this.json.klaviyo_public_api_key,
      "properties": {
        "$email": email,
        ...customFields
      }
    }
    
    const base64String = btoa(JSON.stringify(updateObj));
                                                
    const options = {method: 'GET', headers: {accept: 'text/html'}};

    fetch(`https://a.klaviyo.com/api/identify?data=${base64String}`, options)
      .then(response => response.json())
      .then(response => console.log(response))
      .catch(err => console.error(err));
  }
  
  /**
   * Hijacks the submission of newsletter forms to send data to Klaviyo.
   * Replaces the default form submission with a custom handler.
   */
  hijackNewsletterForms() {
    this.newsletterForms.forEach(form => {
      const submitListener = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        form.classList.add('klaviyo-loading');
        
        const listId = this.json.newsletter_klaviyo_list_id;
        const email = form.querySelector(this.newsletterFields.email).value;
        
        const customFields = {};
        const $fields = [];
        
        // Collect any custom fields defined in newsletterFields.custom
        this.newsletterFields.custom.forEach(field => {
          const fieldElement = form.querySelector(field.selector);
          
          if (fieldElement) {
            customFields[field.name] = fieldElement.value;
            $fields.push(field.name);
          }
        });
  
        const data = {
          $fields,
          ...customFields
        };
        
        // Send the subscription request
        this.subscribe(listId, email, data).then(res => {
          this.onNewsletterSubscribed({form, success: res.success});
        }).finally(() => {
          form.classList.remove('klaviyo-loading');
        });
      };
      
      form.addEventListener('submit', submitListener);
      this.eventListeners.push({ element: form, type: 'submit', listener: submitListener });
    });
  }
  
  /**
   * Handles the response after attempting to subscribe to the newsletter.
   * Displays a success or error message and removes the form.
   * @param {Object} param0 - An object containing the form and success status.
   */
  onNewsletterSubscribed({ form, success }) {
    const message = success 
      ? "Thank you for subscribing to our newsletter!"
      : "An error occurred while subscribing. Please try again.";
    this.showMessage(success, message, form);
  }
  
  /**
   * Credit https://github.com/estrattonbailey/klaviyo-subscribe/tree/master
   * Subscribes a user to a Klaviyo list using the Klaviyo subscribe API.
   * @param {String} listId - The Klaviyo list ID to subscribe to.
   * @param {String} email - The user's email address.
   * @param {Object} params - Additional parameters to include in the subscription.
   * @returns {Promise<Object>} - The response from the Klaviyo API.
   *
   * Refactor idea: https://developers.klaviyo.com/en/reference/create_client_subscription
   */
  async subscribe(listId, email, params = {}) {
    const config = Object.assign({}, params, {
      g: listId,
      email
    });
  
    const body = new URLSearchParams();
    Object.keys(config).forEach(key => {
      body.append(key, config[key]);
    });
  
    const res = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
      },
      body
    });
    
    return await res.json();
  }
  
  showMessage(success, message, targetElement) {
    if (this.feedbackTemplate) {
      // Clear any existing content in the shadow root
      while (this.messageContainer.shadowRoot.firstChild) {
        this.messageContainer.shadowRoot.removeChild(this.messageContainer.shadowRoot.firstChild);
      }
      
      // Clone the template content
      const content = this.feedbackTemplate.content.cloneNode(true);
      
      // Create and append the message element
      const messageElement = document.createElement('span');
      messageElement.className = success ? 'klaviyo-success' : 'klaviyo-error';
      messageElement.textContent = message;
      content.querySelector('[part="content"]').appendChild(messageElement);
      
      // Append the content to the shadow root
      this.messageContainer.shadowRoot.appendChild(content);
      
      // Insert the message container after the target element
      targetElement.insertAdjacentElement('afterend', this.messageContainer);
    } else {
      console.warn('Feedback template not found');
    }
    
    // Remove the target element to prevent duplicate submissions
    targetElement.remove();
  }
  
  /**
   * Defines the selectors for the email field and any custom fields in the BIS form.
   */
  get bisFields() {
    return {
      email: 'input[type="email"]',
      custom: [
        {
          selector: 'select[name="Store"]',
          name: 'Store'
        }
      ],
    };
  }
  
  /**
   * Defines the selectors for the email field and any custom fields in the newsletter form.
   */
  get newsletterFields() {
    return {
      email: 'input[type="email"]',
      custom: [
        {
          selector: 'select[name="Store"]',
          name: 'Store'
        }
      ],
    };
  }
}

const klaviyo = new Klaviyo();
klaviyo.init();

// Re-initialize Klaviyo forms whenever the variant changes
document.addEventListener('variant:change', () => {
  setTimeout(() => {
    klaviyo.init();
  }, 500);
});